import { useEffect, useState } from 'react'
import { Form, Formik, FormikHelpers } from 'formik'
import qs from 'qs'
import { useRouter } from 'next/router'
import { useQuery } from '@apollo/client'
import { useDebouncedCallback } from 'use-debounce'
import { MultiValue } from 'react-select'

import Button from 'components/Button'
import { FormField } from 'components/FormControls'
import { BadgeColor } from 'components/Badge'
import BadgeList from 'components/BadgeList'
import LabelBadgeList from 'components/LabelBadgeList'
import CheckboxDropdown from 'components/CheckboxDropdown'

import setUrlParams from 'lib/set-url-params'
import clearUrlParams from 'lib/clear-url-params'
import GET_CUSTOM_TAGS_LIST from './graphql/GetCustomTagsList.graphql'
import { GetCustomTagsList, GetCustomTagsListVariables } from './graphql/__generated__/GetCustomTagsList'
import {
  ProductFilterArguments,
  ProductBadgeEnum,
  PricingRequestStatusEnum,
  RepPricingRequestStatusEnum,
  VarietyHiddenModeEnum,
  StockOnHandModeEnum
} from '../../../../../../__generated__/globalTypes'
import { BuyerRequestStatus } from 'types/PricingRequest'
import { productBadgeOptions } from 'types/product-badges'
import { ProductBadge } from 'types/Product'

import styles from './ProductsFilter.module.css'

export type ProductsFilterProps = {
  catalogId: string
  baseUrl?: string
  filter?: ProductFilterArguments
}

const ProductsFilter = ({ catalogId, baseUrl = document.location.href, filter }: ProductsFilterProps) => {
  const router = useRouter()

  const [hiddenOrStockDropdown, setHiddenOrStockDropdown] =
    useState<MultiValue<{ label: string; value: string } | undefined>>()

  const submitSearch = useDebouncedCallback(
    (values: ProductFilterArguments, { setSubmitting }: FormikHelpers<ProductFilterArguments>) => {
      setSubmitting(true)
      changeUrl(values)
      setSubmitting(false)
    },
    300
  )

  const changeUrl = (values: ProductFilterArguments) => {
    const url = new URL(baseUrl, document.location.origin)
    const parts = qs.parse(url.search.replace('?', ''))
    const params: Record<string, string | string[] | undefined | null | boolean | number> = {
      ...parts,
      ...values
    }
    setUrlParams(params)

    // always start from page 1 when changing the filter
    delete params.page

    const href = `${url.origin}${url.pathname}?${qs.stringify(params)}`.replace(/\?$/, '')
    router.push(href)
  }

  const initialValues: ProductFilterArguments = {
    badges: [],
    customTags: [],
    search: '',
    ...filter
  }

  const { data } = useQuery<GetCustomTagsList, GetCustomTagsListVariables>(GET_CUSTOM_TAGS_LIST, {
    variables: {
      catalogId
    }
  })

  const availableCustomTags =
    data?.currentSeller?.catalog?.customTags?.map(c => {
      return { value: c.name, label: c.name }
    }) || []

  const hiddenOrStockOptions = [
    { label: 'Hidden', value: VarietyHiddenModeEnum.ONLY_HIDDEN },
    { label: 'Out of Stock', value: StockOnHandModeEnum.OUT_OF_STOCK }
  ]

  useEffect(() => {
    const result = []
    if (filter?.hiddenMode) {
      result.push({ label: 'Hidden', value: VarietyHiddenModeEnum.ONLY_HIDDEN })
    }
    if (filter?.stockOnHandMode) {
      result.push({ label: 'Out of Stock', value: StockOnHandModeEnum.OUT_OF_STOCK })
    }
    setHiddenOrStockDropdown(result)
  }, [filter?.hiddenMode, filter?.stockOnHandMode])

  return (
    <>
      <Formik initialValues={initialValues} onSubmit={submitSearch}>
        {({ submitForm, values, setFieldValue }) => {
          const hiddenOrStockBadgeOptions = (hiddenOrStockDropdown?.map(item => item?.label) as string[]) ?? []

          const handleClearFilters = () => {
            setFieldValue('search', null)
            setFieldValue('customTags', null)
            setFieldValue('badges', null)
            setFieldValue('hiddenMode', null)
            setFieldValue('stockOnHandMode', null)
            clearUrlParams()
            setTimeout(submitForm, 250)
          }

          return (
            <Form className={styles.form}>
              <div className={styles.filterContainer}>
                <div className={styles.dropdownWrapper}>
                  <FormField
                    name="search"
                    type="text"
                    iconRight="search"
                    kind="primary"
                    className={styles.searchInput}
                    placeholder="Search Products"
                    // NOTE: this is a fix for Safari, which seems to have an issue submitting the form on enter
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                      e.key === 'Enter' ? setTimeout(submitForm, 100) : undefined
                    }
                  />
                  <FormField
                    name="customTags"
                    type="checkboxdropdown"
                    placeholder="Custom Tags"
                    className={styles.customTagsDropdown}
                    options={availableCustomTags}
                    onChange={() => setTimeout(submitForm, 250)}
                  />
                  <FormField
                    name="badges"
                    type="checkboxdropdown"
                    placeholder="Labels"
                    className={styles.labelsDropdown}
                    options={productBadgeOptions}
                    onChange={() => setTimeout(submitForm, 250)}
                  />
                  <CheckboxDropdown
                    className={styles.hiddenOrStockDropdown}
                    title="Hidden / Stock"
                    kind="default"
                    options={hiddenOrStockOptions}
                    value={hiddenOrStockDropdown}
                    onChange={(options: MultiValue<{ label: string; value: string } | undefined>) => {
                      setHiddenOrStockDropdown(options)
                      const hiddenOrStock = options.map(item => item?.value)

                      if (hiddenOrStock.includes(VarietyHiddenModeEnum.ONLY_HIDDEN)) {
                        setFieldValue('hiddenMode', VarietyHiddenModeEnum.ONLY_HIDDEN)
                      } else {
                        setFieldValue('hiddenMode', null)
                      }
                      if (hiddenOrStock.includes(StockOnHandModeEnum.OUT_OF_STOCK)) {
                        setFieldValue('stockOnHandMode', StockOnHandModeEnum.OUT_OF_STOCK)
                      } else {
                        setFieldValue('stockOnHandMode', null)
                      }

                      setTimeout(submitForm, 250)
                    }}
                  />
                  <Button kind="link" type="button" className={styles.clearFiltersButton} onClick={handleClearFilters}>
                    Clear Filters
                  </Button>
                </div>

                {/* Add a dummy submit button, so pressing enter will submit the form */}
                <Button isBlock kind="primary" type="submit" className={styles.hideSubmitButton} />
              </div>
              <div className={styles.badgeContainer}>
                <BadgeList
                  name="badgelist-customTags"
                  color={BadgeColor.blue}
                  style="tinted"
                  prefix="Custom Tags"
                  options={values.customTags}
                  onDismissClick={(_option: string, index: number) => {
                    const tags = [...(values.customTags || [])]
                    tags?.splice(index, 1) ?? []
                    setFieldValue('customTags', tags)
                    setTimeout(submitForm, 250)
                  }}
                  hasMargin={true}
                />

                <LabelBadgeList
                  ids={values.badges || []}
                  style="solid"
                  hasMargin={true}
                  onDismissClick={(
                    _option:
                      | ProductBadge
                      | ProductBadgeEnum
                      | PricingRequestStatusEnum
                      | RepPricingRequestStatusEnum
                      | BuyerRequestStatus,
                    index: number
                  ) => {
                    const badges = [...(values.badges || [])]
                    badges.splice(index, 1) ?? []
                    setFieldValue('badges', badges)
                    setTimeout(submitForm, 250)
                  }}
                />

                <BadgeList
                  name="badgelist-hiddenOrStock"
                  color={BadgeColor.yellow}
                  style="tinted"
                  prefix="Filter"
                  options={hiddenOrStockBadgeOptions}
                  onDismissClick={(_option: string, index: number) => {
                    const tags = [...(hiddenOrStockBadgeOptions || [])]
                    const removedTag = tags?.splice(index, 1) ?? []
                    if (removedTag[0] === 'Hidden') {
                      setFieldValue('hiddenMode', null)
                    } else {
                      setFieldValue('stockOnHandMode', null)
                    }
                    setTimeout(submitForm, 250)
                  }}
                  hasMargin={true}
                />
              </div>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default ProductsFilter
